import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import TransitionGroup from 'react-transition-group/TransitionGroup';
import Swipeable from 'react-swipeable';
import Color from 'color';
import throttle from 'lodash-es/throttle';

import ArrowControls from './ArrowControls';
import Slide from './Slide';
import SwipeAnimationOverlay from './SwipeAnimationOverlay';

import apiActions from '../../common/actions/apiActions';
import embedActions from '../actions/embedActions';
import * as navigationActions from '../../common/actions/navigationActions';
import handleError from '../../common/actions/errorActions';
import styles from '../styles';
import utils from '../../common/utils';
import useWindowFocus from '../../common/hooks/useWindowFocus';
import variables from '../../common/variables';
import AppLoader from '../../common/components/AppLoader';

import {
  getTransitionFromTransitionConfig,
  TRANSITION_PHASE_ENTER,
  TRANSITION_PHASE_EXIT,
  reverse,
} from '../../common/transitions';

const scrollSlideInView = (index, instant) => {
  const element = document.querySelector(`.slide-container.slide${index + 1}`);
  if (element) {
    if (instant) {
      element.scrollIntoView({ block: 'center' });
    } else {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }
};
//
///
const Viewer = props => {
  const [scrollableViewer, setScrollableViewer] = useState(
    props.scrollableViewer,
  );
  const [dimensions, setDimensions] = useState(props.dimensions);
  const [previousSlideIndex, setPreviousSlideIndex] = useState(-1);
  const [swipeDeltaX, setSwipeDeltaX] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);

  const windowHasFocus = useWindowFocus();

  React.useEffect(() => {
    const onScroll = e => {
      setScrollTop(e.target.documentElement.scrollTop);
    };

    const onScrollThrottled = throttle(onScroll, 250, {
      leading: true,
      trailing: true,
    });

    window.addEventListener('scroll', onScrollThrottled);

    return () => window.removeEventListener('scroll', onScrollThrottled);
  }, []);

  React.useEffect(() => {
    setDimensions(props.dimensions);
  }, [props.dimensions]);

  React.useEffect(() => {
    setScrollableViewer(props.scrollableViewer);
  }, [props.scrollableViewer]);

  React.useEffect(() => {
    if (!scrollableViewer) {
      document.querySelector('#app').style.position = 'fixed';
      if (props.index !== props.previousSlideIndex) {
        setSwipeDeltaX(0);
        setPreviousSlideIndex(props.index);
      }

      if (props.transitioning && props.index < props.previousSlideIndex) {
        reverse();
      }
    } else {
      document.querySelector('#app').style.position = 'relative';
      if (props.slideChangedBy !== 'MOUSE_WHEEL') {
        scrollSlideInView(props.index);
      }
    }
  }, [props.index, props.initialized, props.slideChangedBy]);

  const onTransitionStart = phase => {
    if (phase === TRANSITION_PHASE_ENTER) {
      props.actions.dispatchTransitionStart();
    }
  };

  const onTransitionComplete = phase => {
    if (phase === TRANSITION_PHASE_ENTER) {
      props.actions.dispatchTransitionComplete();
    }
  };

  const onSwiping = (e, deltaX, isFlick) => {
    if (document.documentElement.clientWidth <= window.innerWidth && isFlick) {
      setSwipeDeltaX(deltaX);
    }
  };

  const onSwipedLeft = (e, deltaX, isFlick) => {
    if (document.documentElement.clientWidth <= window.innerWidth) {
      if (isFlick) {
        props.actions.dispatchNextSlide();
      }
    }
  };

  const onSwipedRight = (e, deltaX, isFlick) => {
    if (document.documentElement.clientWidth <= window.innerWidth) {
      if (isFlick) {
        props.actions.dispatchPreviousSlide();
      }
    }
  };

  if (props.initialized) {
    const slideWrapperStyles = {
      ...styles.slideWrapper,
      position: 'relative',
      left: 0,
      top: 0,
      width: `${dimensions.containerWidth * dimensions.scale}px`,
      height: `${dimensions.containerHeight * dimensions.scale}px`,
      transform: ` translateX(${dimensions.horizontalOffset}px) scale(${
        dimensions.horizontalOffset ? 0.99 : 1
      })`,
    };

    const transitionsEnabled = props.transitionsEnabled;
    const currentSlideIndex = props.index;
    const currentSlide = props.slides[currentSlideIndex];
    const previousSlide =
      previousSlideIndex >= 0 ? props.slides[previousSlideIndex] : null;
    const previousSlideTransition = previousSlide
      ? previousSlide.transition
      : null;
    const currentSlideTransition = currentSlide.transition;
    const replaceMap = {
      half_width: dimensions.width / 2,
    };
    const enterTransition = getTransitionFromTransitionConfig(
      previousSlideTransition,
      TRANSITION_PHASE_ENTER,
      replaceMap,
    );
    const exitTransition = getTransitionFromTransitionConfig(
      currentSlideTransition,
      TRANSITION_PHASE_EXIT,
      replaceMap,
    );
    const currentSlideData = currentSlide.content;

    const bgColor = currentSlide.content.background;
    let darkenBgColor = null;
    if (bgColor) {
      darkenBgColor = Color(currentSlide.content.background)
        .rgb()
        .mix(Color('rgba(0,0,0,1)').rgb(), 0.05)
        .toString();
    }
    const currentSlideColor = darkenBgColor || variables.colors.black;
    const shouldShowSwipeAnimation = utils.isMobileDevice();

    const slideContainer = {
      ...styles.container,
      ...(!scrollableViewer && {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        transition: 'all 0.4s',
      }),
      backgroundColor: currentSlideColor,
    };

    if (scrollableViewer) {
      return (
        <>
          <div style={slideContainer} onMouseUp={props.onClick}>
            {props.slides.map((slide, index) => (
              <Slide
                scrollableViewer={scrollableViewer}
                scrollTop={scrollTop}
                slideId={slide.id}
                key={`slide-${slide.id}`}
                number={index + 1}
                data={slide.content}
                appInstanceId={props.appInstanceId}
                globalScale={dimensions.scale}
                windowHasFocus={windowHasFocus}
                isFullScreen={props.isFullScreen}
                dimensions={dimensions}
              />
            ))}
          </div>
        </>
      );
    }

    return (
      <>
        {utils.isMobileDevice() && (
          <ArrowControls
            visible={props.shouldShowControls}
            swipeDeltaX={swipeDeltaX}
            width={dimensions.width}
          />
        )}

        <Swipeable
          style={slideContainer}
          onSwiping={onSwiping}
          onSwipedLeft={onSwipedLeft}
          onSwipedRight={onSwipedRight}
          onClick={props.onClick}
          onTap={props.onTap}
          flickThreshold={0.1}
          stopPropagation
          preventDefaultTouchmoveEvent={
            document.documentElement.clientWidth <= window.innerWidth
          }
        >
          <TransitionGroup
            style={slideWrapperStyles}
            childFactory={child =>
              React.cloneElement(child, {
                enter: transitionsEnabled,
                exit: transitionsEnabled,
              })
            }
          >
            <Slide
              slideId={currentSlide.id}
              key={`slide-${currentSlideIndex}`}
              number={currentSlideIndex + 1}
              data={currentSlideData}
              timeout={{
                enter: enterTransition ? null : 0,
                exit: exitTransition ? null : 0,
              }}
              appInstanceId={props.appInstanceId}
              globalScale={dimensions.scale}
              windowHasFocus={windowHasFocus}
              enterTransition={enterTransition}
              exitTransition={exitTransition}
              onTransitionStart={onTransitionStart}
              onTransitionComplete={onTransitionComplete}
              isFullScreen={props.isFullScreen}
              dimensions={dimensions}
            />
          </TransitionGroup>
        </Swipeable>

        {utils.isMobileDevice() && (
          <SwipeAnimationOverlay showIfNeeded={shouldShowSwipeAnimation} />
        )}
      </>
    );
  }

  return <AppLoader color="white" />;
};

Viewer.propTypes = {
  actions: PropTypes.objectOf(PropTypes.func).isRequired,
  appInstanceId: PropTypes.string.isRequired,

  initialized: PropTypes.bool.isRequired,
  index: PropTypes.number.isRequired,
  transitioning: PropTypes.bool.isRequired,
  transitionsEnabled: PropTypes.bool.isRequired,
  slides: PropTypes.arrayOf(PropTypes.object).isRequired,
  isFullScreen: PropTypes.bool.isRequired,
  scrollableViewer: PropTypes.bool,
  onClick: PropTypes.func,
  onTap: PropTypes.func,
  slideChangedBy: PropTypes.string,
  dimensions: PropTypes.object,
  previousSlideIndex: PropTypes.number,
  shouldShowControls: PropTypes.bool,
};

Viewer.defaultProps = {
  user: null,
};

const mapStateToProps = (state, props) => {
  return {
    appInstanceId: state.appReducer.appInstanceId,
    isOffline: state.appReducer.isOffline,
    index: state.slideReducer.index,
    hideControls: state.slideReducer.hideControls,
    scrollableViewer: state.slideReducer.scrollableViewer,
    slideChangedBy: state.slideReducer.slideChangedBy,
    transitionsEnabled: state.slideReducer.transitionsEnabled,
    transitioning: state.slideReducer.transitioning,
    slides: state.slideReducer.slides,
    isFullScreen: state.fullScreenReducer,
    updatedAt: state.slideReducer.updatedAt,
    user: state.slideReducer.user,
    team: state.slideReducer.team,
    transitions: state.transitionsReducer,
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      handleError,
      ...apiActions,
      ...embedActions,
      ...navigationActions,
    },
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(React.memo(Viewer));
